import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { DayPicker, type DateRange } from 'react-day-picker'
import { Clock, CalendarDays, ExternalLink } from 'lucide-react'
import { Select } from '../../components/ui/Select'
import { it as itLoc, enGB, de as deLoc } from 'date-fns/locale'
import { differenceInCalendarDays } from 'date-fns'
import { getMainContext } from '../../lib/api/booking'
import { useBooking } from '../../store/bookingStore'
import { StepShell } from '../../components/layout/StepShell'
import { Skeleton, ErrorState } from '../../components/ui/States'
import { buildTimeSlots, hhmm, parseDay, toDay } from '../../lib/format'
import { stepPath } from '../../lib/steps'
import type { Locale, TransportContext } from '../../lib/api/types'
import { TransportIcon } from '../../components/ui/TransportIcon'
import { cn } from '../../lib/cn'

const DF = { it: itLoc, en: enGB, de: deLoc }

export function DatesStep() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const navigate = useNavigate()
  const { locale: routeLocale = 'it' } = useParams()

  const cart = useBooking((s) => s.cart)
  const setContexts = useBooking((s) => s.setContexts)
  const setTransport = useBooking((s) => s.setTransport)
  const setDates = useBooking((s) => s.setDates)

  const { data, isLoading, isError, refetch } = useQuery({
    // Locale is part of the key so switching language refetches the backend labels (Auto/Car/…)
    // instead of showing the stale cached ones. keepPreviousData avoids a skeleton flash on switch.
    queryKey: ['mainContext', locale],
    queryFn: getMainContext,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (data) setContexts(data)
  }, [data, setContexts])

  const [transportId, setTransportId] = useState<number | null>(cart.transportId)
  const [range, setRange] = useState<DateRange | undefined>(
    cart.fromDay ? { from: parseDay(cart.fromDay), to: cart.toDay ? parseDay(cart.toDay) : undefined } : undefined,
  )
  const [time, setTime] = useState<string>(cart.fromHour ?? '')
  const [timeTouched, setTimeTouched] = useState(false) // false until the user actively picks a time → prompt them to

  // default-select the first transport once data arrives
  useEffect(() => {
    if (data?.length && transportId == null) setTransportId(data[0].transport.id)
  }, [data, transportId])

  const ctx: TransportContext | undefined = useMemo(
    () => data?.find((c) => c.transport.id === transportId),
    [data, transportId],
  )

  // default time to the transport's earliest allowed slot
  useEffect(() => {
    if (ctx && !time) setTime(ctx.minTime)
  }, [ctx, time])

  const timeSlots = useMemo(() => {
    if (!ctx) return []
    const onMinDay = range?.from && toDay(range.from) === ctx.minDate
    return buildTimeSlots(ctx.bookingHoursSubdivision, onMinDay ? ctx.minTime : '00:00:00')
  }, [ctx, range?.from])

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0
  const overMax = ctx ? nights > ctx.bookingMaxDays : false
  const canContinue = Boolean(transportId && range?.from && range?.to && time && !overMax)

  function onContinue() {
    if (!canContinue || !range?.from || !range?.to || !transportId) return
    if (transportId !== cart.transportId) setTransport(transportId)
    setDates(toDay(range.from), time, toDay(range.to))
    navigate(stepPath(routeLocale, 'rate'))
  }

  return (
    <StepShell
      stepKey="dates"
      title={t('dates.title')}
      subtitle={t('dates.subtitle')}
      showPrice={false}
      primaryLabel={t('dates.checkAvailability')}
      onPrimary={onContinue}
      primaryDisabled={!canContinue}
    >
      {isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : (
        // vehicle spans the top; the calendar (left) and time/summary (right) sit side-by-side below,
        // so the "Orario" column lines up with the CALENDAR (not the vehicle cards)
        <div className="space-y-6">
          {/* transport selector — full-width top row */}
          <section>
            <p className="gsm-label">{t('dates.vehicle')}</p>
            <div className="grid grid-cols-2 gap-3 sm:max-w-md">
              {isLoading
                ? [0, 1].map((i) => <Skeleton key={i} className="h-28" />)
                : data?.map((c) => {
                    const active = c.transport.id === transportId
                    return (
                      <button
                        key={c.transport.id}
                        onClick={() => setTransportId(c.transport.id)}
                        // Stacked: icon on top, label below on its own full-width line — so a long
                        // name (Motorbike / Motorrad) wraps cleanly instead of breaking the card.
                        className={cn(
                          'group flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 bg-white p-4 text-center transition-all',
                          active
                            ? 'border-brand-600 shadow-card-hover'
                            : 'border-ink-100 hover:border-ink-200 hover:shadow-card',
                        )}
                      >
                        <span className="flex h-12 w-16 flex-none items-center justify-center">
                          <TransportIcon iconUri={c.transport.iconUri} label={c.transport.label} />
                        </span>
                        <span className="w-full font-display font-bold leading-tight text-ink-900">{c.transport.label}</span>
                      </button>
                    )
                  })}
            </div>
          </section>

          {/* calendar (left) + time/summary (right) — the sidebar aligns with the calendar top */}
          {/* calendar (left) + Orario (right sidebar) using the SAME proportions as the other steps'
              content + summary layout, so every step is the same overall width. minmax(0,1fr) keeps the
              calendar column stable → no width jump between the loading skeleton and the loaded month. */}
          <div className="space-y-6 lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-10 lg:space-y-0 xl:grid-cols-[minmax(0,1fr)_24rem] xl:gap-14">
            <section>
              <div className="gsm-card p-3 sm:p-4">
                <div className="mb-2 flex items-center gap-2 px-1 text-sm font-semibold text-ink-700">
                  <CalendarDays className="h-4 w-4 text-brand-600" /> {t('dates.selectRange')}
                </div>
                {isLoading || !ctx ? (
                  // match the loaded calendar's footprint so the loading state doesn't look squeezed
                  <Skeleton className="h-[340px] w-full" />
                ) : (
                  <DayPicker
                    className="w-full"
                    mode="range"
                    locale={DF[locale]}
                    numberOfMonths={1}
                    weekStartsOn={1}
                    selected={range}
                    onSelect={setRange}
                    startMonth={parseDay(ctx.minDate)}
                    disabled={[{ before: parseDay(ctx.minDate) }, { after: parseDay(ctx.maxDate) }]}
                  />
                )}
              </div>
            </section>

            {/* time + arrival/departure, aligned with the calendar. On desktop it becomes a larger,
                self-contained panel (card + bigger type); mobile stays inline and compact. */}
            <section className="space-y-2.5 lg:sticky lg:top-24 lg:space-y-3.5 lg:rounded-2xl lg:border lg:border-ink-100 lg:bg-white lg:p-5 lg:shadow-card">
              <label className="gsm-label flex items-center gap-1.5 lg:text-[13.5px]">
                <Clock className="h-3.5 w-3.5 lg:h-[18px] lg:w-[18px]" /> {t('dates.time')}
              </label>
              <Select
                ariaLabel={t('dates.time')}
                value={time}
                options={timeSlots.map((s) => ({ value: s, label: hhmm(s) }))}
                onChange={(v) => {
                  setTime(v)
                  setTimeTouched(true)
                }}
                icon={<Clock className="h-4 w-4 text-brand-600" />}
                openUp
                disabled={!ctx}
                // draw attention to the time until the user picks one — this is easy to miss
                className={cn('rounded-xl transition', ctx && !timeTouched && 'ring-2 ring-brand-500 ring-offset-2 ring-offset-white')}
              />
              {ctx && !timeTouched && (
                <p className="flex items-center gap-1.5 text-xs font-semibold text-brand-600">
                  <Clock className="h-3.5 w-3.5" /> {t('dates.pickTimeHint')}
                </p>
              )}
              {nights > 0 && (
                <p className={cn('text-sm lg:text-[15px] lg:font-semibold', overMax ? 'text-brand-600' : 'text-ink-500')}>
                  {nights} {nights === 1 ? t('common.day') : t('common.days')}
                  {overMax && ctx ? ` · max ${ctx.bookingMaxDays}` : ''}
                </p>
              )}

              {/* selected period — compact on mobile, larger rows on desktop */}
              <div className="grid grid-cols-2 gap-2 pt-1 lg:grid-cols-1 lg:gap-2.5 lg:border-t lg:border-ink-100 lg:pt-3.5">
                <CompactDate label={t('dates.arrival')} value={range?.from ? formatLong(range.from, locale) : '—'} active={Boolean(range?.from)} />
                <CompactDate label={t('dates.departure')} value={range?.to ? formatLong(range.to, locale) : '—'} active={Boolean(range?.to)} />
              </div>
            </section>
          </div>

          {/* mobile-only fallback to the classic booking site, under the form (desktop uses the header) */}
          <div className="pt-1 text-center sm:hidden">
            <a
              href={`https://booking.garagesanmarco.it/${routeLocale}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-4 py-2 text-sm font-semibold text-brand-700 ring-1 ring-inset ring-brand-200 transition-colors hover:bg-brand-100 hover:ring-brand-300"
            >
              {t('common.classicSite')} <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </StepShell>
  )
}

function CompactDate({ label, value, active }: { label: string; value: string; active?: boolean }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-2 rounded-lg bg-white px-3 py-1.5 ring-1 transition lg:rounded-xl lg:bg-ink-50 lg:px-4 lg:py-3', active ? 'ring-brand-200 lg:ring-brand-300' : 'ring-ink-100 lg:ring-transparent')}>
      <span className="text-[11px] font-medium text-ink-400 lg:text-[13px]">{label}</span>
      <span className={cn('truncate text-[12.5px] font-semibold capitalize lg:text-[15px]', active ? 'text-ink-900' : 'text-ink-300')}>
        {value}
      </span>
    </div>
  )
}

function formatLong(d: Date, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === 'it' ? 'it-IT' : locale === 'de' ? 'de-DE' : 'en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d)
}
